import { useState, useCallback, useEffect } from 'react';
import { message } from 'antd';
import { c203Api, FC240 } from '../../../services/c203Api';

export type Mode = 'browse' | 'add' | 'edit';

const SHIP_TYPE_OPTIONS = [
  { value: '01', label: '內銷出貨' },
  { value: '02', label: '外銷報關出貨' },
  { value: '03', label: '快遞出貨' },
  { value: '04', label: '客戶寄庫' },
  { value: '05', label: '其它' },
];

const INVOICE_OPTIONS = [
  { value: '1', label: '三聯式' },
  { value: '2', label: '二聯式' },
  { value: '3', label: '外銷發票-零稅率' },
  { value: '4', label: '不開票-內銷' },
  { value: '5', label: '不開票-外銷' },
];

export function useShippingForm() {
  const [mode, setMode] = useState<Mode>('browse');
  const [records, setRecords] = useState<FC240[]>([]);
  const [currentIdx, setCurrentIdx] = useState(-1);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<Partial<FC240>>({});
  const [lastApiResponse, setLastApiResponse] = useState<unknown>(null);

  const current = currentIdx >= 0 && currentIdx < records.length ? records[currentIdx] : null;
  const isEditing = mode !== 'browse';

  const loadRecords = useCallback(async (keyword?: string) => {
    setLoading(true);
    try {
      const res = await c203Api.list(1, 500, keyword);
      setLastApiResponse(res);
      setRecords(res.data || []);
      return res.data || [];
    } catch {
      message.error('載入失敗');
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (current && mode === 'browse') {
      setFormData({ ...current });
    }
  }, [current, mode]);

  useEffect(() => {
    loadRecords().then(data => {
      if (data.length > 0) setCurrentIdx(0);
    });
  }, [loadRecords]);

  const navFirst = () => { if (!isEditing && records.length > 0) setCurrentIdx(0); };
  const navPrev = () => { if (!isEditing && currentIdx > 0) setCurrentIdx(currentIdx - 1); };
  const navNext = () => { if (!isEditing && currentIdx < records.length - 1) setCurrentIdx(currentIdx + 1); };
  const navLast = () => { if (!isEditing && records.length > 0) setCurrentIdx(records.length - 1); };

  const startAdd = () => {
    setMode('add');
    setFormData({
      fc240_02: '02',
      fc240_19: 1,
      fc240_80: '',
      details: [],
    });
  };

  const startEdit = () => {
    if (!current) return;
    if (current.fc240_80 === 'P' || current.fc240_80 === 'C') {
      message.warning('已核准或已取消的單據不可修改');
      return;
    }
    setMode('edit');
    setFormData({ ...current });
  };

  const cancel = () => {
    setMode('browse');
    if (current) setFormData({ ...current });
  };

  const save = async () => {
    try {
      if (mode === 'add') {
        const created = await c203Api.create(formData);
        setLastApiResponse(created);
        message.success('新增成功');
        const data = await loadRecords();
        const idx = data.findIndex(r => r.fc240_01 === created.fc240_01);
        setCurrentIdx(idx >= 0 ? idx : 0);
      } else if (current) {
        const updated = await c203Api.update(current.fc240_01, formData);
        setLastApiResponse(updated);
        message.success('修改成功');
        const data = await loadRecords();
        const idx = data.findIndex(r => r.fc240_01 === current.fc240_01);
        setCurrentIdx(idx >= 0 ? idx : 0);
      }
      setMode('browse');
    } catch (err: any) {
      const vErrs = err?.response?.data?.errors;
      if (vErrs && Array.isArray(vErrs)) {
        vErrs.forEach((e: any) => message.error(e.message));
      } else {
        message.error(err?.response?.data?.error || '儲存失敗');
      }
    }
  };

  const doDelete = async () => {
    if (!current) return;
    try {
      await c203Api.delete(current.fc240_01);
      setLastApiResponse({ deleted: current.fc240_01 });
      message.success('刪除成功');
      const data = await loadRecords();
      if (data.length > 0) {
        setCurrentIdx(Math.min(currentIdx, data.length - 1));
      } else {
        setCurrentIdx(-1);
      }
    } catch {
      message.error('刪除失敗');
    }
  };

  const submitOrder = async () => {
    if (!current) return;
    try {
      const response = await c203Api.updateStatus(current.fc240_01, 'D');
      setLastApiResponse(response);
      message.success('送審成功');
      await loadRecords();
    } catch (err: any) {
      message.error(err?.response?.data?.error || '送審失敗');
    }
  };

  const withdrawOrder = async () => {
    if (!current) return;
    try {
      const response = await c203Api.updateStatus(current.fc240_01, 'N');
      setLastApiResponse(response);
      message.success('取回成功');
      await loadRecords();
    } catch (err: any) {
      message.error(err?.response?.data?.error || '取回失敗');
    }
  };

  const updateField = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const importSource = async (sourceType: 'export' | 'domestic', orderNo: string) => {
    if (mode !== 'add') return;
    const normalized = orderNo.trim();
    if (!normalized) { message.warning('請輸入來源單號'); return; }
    setLoading(true);
    try {
      const preview = await c203Api.importSource(sourceType, normalized);
      setLastApiResponse(preview);
      setFormData(prev => ({ ...prev, fc240_10: preview.fc240_10,
        fc240_12: preview.fc240_12, fc240_18: preview.fc240_18,
        fc240_19: preview.fc240_19, fc240_34: preview.fc240_34,
        details: preview.details }));
      message.success(`已帶入 ${preview.details?.length || 0} 筆來源明細（尚未儲存）`);
    } catch (err: any) {
      message.error(err?.response?.data?.error || '來源單帶入失敗');
    } finally { setLoading(false); }
  };

  const statusLabel = (status: string) => {
    switch (status) {
      case 'D': return '送審中';
      case 'P': return '已核准';
      case 'C': return '已取消';
      default: return '草稿';
    }
  };

  return {
    mode, isEditing, loading,
    records, current, currentIdx,
    formData, updateField, importSource,
    navFirst, navPrev, navNext, navLast,
    startAdd, startEdit, cancel, save,
    doDelete, submitOrder, withdrawOrder,
    loadRecords, statusLabel,
    SHIP_TYPE_OPTIONS, INVOICE_OPTIONS,
    lastApiResponse,
  };
}
